import styled from "@emotion/styled";
import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import theme from "../theme";
import { useWindowSize } from "@reactuses/core";
import { useI18n } from "../locales";

const { md } = theme.screen;

const AlertModal: React.FC<{
  open: boolean;
  onPressNo?: () => void;
  onPressYes?: () => void;
  title: string;
  content: string;
  content2?: string;
  content3?: string;
  confirmText: string | JSX.Element;
  icon: JSX.Element;
  other?: JSX.Element;
  otherFilled?: boolean;
}> = (props) => {
  const { t } = useI18n();
  const {
    open,
    onPressNo,
    onPressYes,
    title,
    content,
    content2,
    content3,
    confirmText,
    icon,
    other,
    otherFilled,
  } = props;
  const { width } = useWindowSize();
  return (
    <Dialog
      open={open}
      onClose={onPressNo}
      PaperProps={{
        sx: {
          width: width <= md ? "400px" : "513px",
          maxWidth: width <= md ? "400px" : "513px",
          minWidth: width <= md ? "320px" : "513px",
          minHeight: width <= md ? "200px" : "350px",
          maxHeight: width <= md ? "450px" : "500px",
          alignItems: "center",
          pt: width <= md ? "40px" : "50px",
          borderRadius: "20px",
          pb: width <= md ? "40px" : "65px",
        },
      }}
    >
      {icon}
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: width <= md ? "14px" : "20px",
          lineHeight: width <= md ? "17px" : "23px",
          mt: width <= md ? "0px" : "14px",
          mb: width <= md ? "0px" : content ? "0px" : "14px",
          padding: width <= md ? "10px 15px" : "16px 24px",
        }}
      >
        {title}
      </DialogTitle>
      {content && (
        <DialogContent
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            paddingBottom: "0px",
            mb: width <= md ? "0px" : "20px",
          }}
        >
          <DialogContentText
            sx={{
              width: {
                xs: "100%",
                sm: "100%",
              },
              marginX: {
                xs: "auto",
              },
              fontWeight: 400,
              fontSize: width <= md ? "12px" : "15px",
              lineHeight: "18px",
              color: theme.color.neutral_600,
              whiteSpace: "pre-line",
              textAlign: "center",
            }}
          >
            {<Content>{content}</Content>}
            {content2 && <Content>{content2}</Content>}
            {content3 && <Content>{content3}</Content>}
          </DialogContentText>
        </DialogContent>
      )}
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          if (onPressYes) {
            onPressYes();
          }
        }}
      >
        {other}
        <BtnWrapper>
          {other ? (
            <Btn2
              // onClick={onPressYes}
              isFilled={otherFilled as boolean}
              disabled={!otherFilled}
              hoverColor={theme.statusColor.MAIN_HOVER}
              type="submit"
            >
              {confirmText}
            </Btn2>
          ) : (
            <Btn
              // onClick={onPressYes}
              bgColor={theme.statusColor.MAIN}
              color={theme.color.white}
              hoverColor={theme.statusColor.MAIN_HOVER}
              type="submit"
            >
              {confirmText}
            </Btn>
          )}
          <Btn
            bgColor={theme.color.neutral_400}
            color={theme.color.white}
            onClick={onPressNo}
            hoverColor={theme.color.neutral_500}
            type="button"
          >
            {t.common.cancel}
          </Btn>
        </BtnWrapper>
      </Form>
    </Dialog>
  );
};

const Btn = styled.button<{
  bgColor: string;
  color: string;
  hoverColor: string;
}>`
  width: 330px;
  height: 54px;
  background: ${({ bgColor }) => bgColor};
  border-radius: 3px;
  color: ${({ color }) => color};
  border: none;
  font-weight: 700;
  font-size: 15px;
  line-height: 19px;
  cursor: pointer;
  &:hover {
    background-color: ${({ hoverColor }) => hoverColor};
  }
  @media (max-width: ${theme.screen.md}px) {
    width: 100%;
    font-size: 12px;
    height: 30px;
  }
`;

const Btn2 = styled.button<{ isFilled: boolean; hoverColor: string }>`
  width: 330px;
  height: 54px;
  background: ${({ isFilled }) =>
    isFilled ? theme.statusColor.MAIN : theme.statusColor.DISABLED};
  border-radius: 3px;
  color: ${theme.color.white};
  border: none;
  font-weight: 700;
  font-size: 15px;
  line-height: 19px;
  cursor: ${({ isFilled }) => (isFilled ? "pointer" : "default")};
  &:hover {
    background-color: ${({ isFilled, hoverColor }) =>
      isFilled ? hoverColor : theme.statusColor.DISABLED};
  }
  @media (max-width: ${theme.screen.md}px) {
    width: 100%;
    font-size: 12px;
    height: 30px;
  }
`;

const Content = styled.span`
  line-height: 22px;
  display: block;
`;

const BtnWrapper = styled.div`
  @media (max-width: ${md}px) {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 15px;
  }
  @media (min-width: ${md + 1}px) {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 20px;
    align-items: center;
  }

  button {
    margin: 0px;
  }
`;

const Form = styled.form`
  @media (max-width: ${md}px) {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 10px 30px 10px 30px;
    gap: 15px;
  }
  @media (min-width: ${md + 1}px) {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }
`;

export default AlertModal;
